import { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { cloneDeep, get, set } from 'lodash';
import { toastErrorMessage } from 'utils/helpers';
import requireValue from 'utils/requireValue';
import { useMutation } from '@apollo/client/react';
import {
  type RequestPersonalOfferMutationVariables,
  RequestPersonalOfferDocument,
  MyBudgetDocument,
  type MyBudgetQuery,
  type MyBudgetQueryVariables,
  FinancingDocument,
  type FinancingQuery,
  type FinancingQueryVariables,
} from 'graphql/generated/graphql';

const useRequestPersonalOffer = (): [
  (variables: RequestPersonalOfferMutationVariables) => Promise<void>,
  boolean,
] => {
  const { formatMessage, locale } = useIntl();
  const [requestPersonalOfferMutation, { loading, client }] = useMutation(
    RequestPersonalOfferDocument,
    {
      onCompleted: () => toast.success(formatMessage({ id: 'loan.request_sent' })),
      onError: toastErrorMessage,
    },
  );

  const handleRequestPersonalOffer = useCallback(
    async ({ projectId, expressLoanId, formData }: RequestPersonalOfferMutationVariables) => {
      // onError has handled a failure: with an onError option the mutation resolved (to undefined) under Apollo
      // Client 2, Apollo Client 4 rejects after calling it.
      const result = await requestPersonalOfferMutation({
        variables: { projectId, expressLoanId, formData },
      }).catch(() => undefined);
      const loanRequest = get(result, 'data.requestPersonalOffer.success');

      if (expressLoanId) return;

      // Bug: after a failed request `loanRequest` is undefined, and the cache entry gets
      // `loan_request: undefined` (set() writes it as the former assignment did).
      if (projectId) {
        const financingData = client.readQuery<FinancingQuery, FinancingQueryVariables>({
          query: FinancingDocument,
          variables: { id: projectId },
        });
        const newFinancingData = requireValue(cloneDeep(financingData), 'project');
        const project = requireValue(newFinancingData.project, 'progress');
        set(project.progress, 'loan_request', loanRequest);
        client.writeQuery<FinancingQuery, FinancingQueryVariables>({
          query: FinancingDocument,
          variables: { id: projectId },
          data: { ...newFinancingData },
        });
      } else {
        const myBudgetData = await client.readQuery<MyBudgetQuery, MyBudgetQueryVariables>({
          query: MyBudgetDocument,
          variables: { locale },
        });
        const data = requireValue(cloneDeep(myBudgetData), 'myBudget');
        set(requireValue(data.myBudget, 'loan_request'), 'loan_request', loanRequest);
        client.writeQuery<MyBudgetQuery, MyBudgetQueryVariables>({
          query: MyBudgetDocument,
          variables: { locale },
          data,
        });
      }
    },
    [client, locale, requestPersonalOfferMutation],
  );
  return [handleRequestPersonalOffer, loading];
};

export default useRequestPersonalOffer;
